'use client';

import { useEffect, useState } from 'react';

import { Box, Button, IconButton, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { SessionItem } from './SessionItem';
import type { ActiveSession } from '../types';

interface SessionLimitModalProps {
  sessions: ActiveSession[];
  isLoading: boolean;
  error: string | null;
  /** Terminates the selected session and proceeds with login. */
  onTerminateSelected: (sessionId: string) => void;
  /** Secondary, de-emphasized action: signs out of every device. */
  onTerminateAll: () => void;
  onClose: () => void;
  onContinue: () => void;
}

export function SessionLimitModal({
  sessions,
  isLoading,
  error,
  onTerminateSelected,
  onTerminateAll,
  onClose,
  onContinue,
}: SessionLimitModalProps) {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  // Keep the selection valid as sessions change (e.g. after a termination).
  useEffect(() => {
    if (
      selectedSessionId &&
      !sessions.some((s) => s.sessionId === selectedSessionId)
    ) {
      setSelectedSessionId(null);
    }
  }, [sessions, selectedSessionId]);

  // If all sessions terminated, show success and allow continue
  if (sessions.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
          maxWidth: 480,
          width: '100%',
          position: 'relative',
        }}
      >
        <Typography variant="h6" fontWeight={500} textAlign="center" mb={2}>
          All sessions terminated
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
          You can now continue to sign in.
        </Typography>
        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            onClick={onContinue}
            disabled={isLoading}
            sx={{
              bgcolor: '#2196F3',
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              '&:hover': { bgcolor: '#1976D2' },
            }}
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: 24,
        p: 4,
        maxWidth: 480,
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        disabled={isLoading}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: 'text.secondary',
        }}
        aria-label="Close"
      >
        <CloseIcon />
      </IconButton>

      {/* Title */}
      <Typography variant="h5" fontWeight={500} mb={1.5} pr={4}>
        Maximum sessions exceeded
      </Typography>

      {/* Subtitle */}
      <Typography variant="body2" color="text.secondary" mb={3}>
        Your account is currently in use on multiple devices. Select a session to
        sign out and free up a slot, then continue signing in.
      </Typography>

      {/* Error message */}
      {error && (
        <Typography variant="body2" color="error" mb={2}>
          {error}
        </Typography>
      )}

      {/* Session list (single-select) */}
      <Box role="radiogroup" aria-label="Active sessions" sx={{ mb: 3 }}>
        {sessions.map((session) => (
          <SessionItem
            key={session.sessionId}
            session={session}
            selected={selectedSessionId === session.sessionId}
            onSelect={() => setSelectedSessionId(session.sessionId)}
            disabled={isLoading}
          />
        ))}
      </Box>

      {/* Primary action: terminate the selected session and continue */}
      <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
        <Button
          variant="contained"
          fullWidth
          onClick={() =>
            selectedSessionId && onTerminateSelected(selectedSessionId)
          }
          disabled={isLoading || !selectedSessionId}
          sx={{
            bgcolor: '#2196F3',
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: 'none',
            '&:hover': { bgcolor: '#1976D2' },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Terminate selected & continue'
          )}
        </Button>

        {/* Secondary, de-emphasized action */}
        <Button
          variant="text"
          size="small"
          onClick={onTerminateAll}
          disabled={isLoading}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
          }}
        >
          Sign out of all devices
        </Button>
      </Box>
    </Box>
  );
}
